import { useState, useCallback, useRef } from 'react';
import Keycloak from 'keycloak-js';
import config from '../config/app.config';
import { USER_ROLES } from '../constants';

const useAuth = () => {
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  
  // Usar ref para almacenar el interval ID y evitar dependencias circulares
  const refreshIntervalRef = useRef(null);
  // Flag para prevenir múltiples inicializaciones concurrentes
  const initializingRef = useRef(false);

  // Función para limpiar el interval de token refresh
  const clearTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Logout - usar useCallback sin dependencias circulares
  const logout = useCallback(async () => {
    console.log('🚪 Iniciando proceso de logout...');
    
    try {
      // Limpiar interval de refresh antes del logout
      clearTokenRefresh();

      // Limpiar el estado local inmediatamente para mejor UX
      console.log('🧹 Limpiando estado local de autenticación...');
      setAuthenticated(false);
      setUser(null);
      setRoles([]);
      setError(null);
      setInitialized(false);
      
      // Limpiar solo datos de autenticación del storage, preservar preferencias
      const keysToRemove = [
        'keycloak-token',
        'keycloak-refresh-token', 
        'keycloak-id-token',
        'keycloak-state',
        'keycloak-nonce',
        'kc-iss',
        'kc-auth-success',
        'KC_RESTART'
      ];
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      console.log('✅ Datos de autenticación limpiados del storage');

      if (keycloak && keycloak.authenticated) {
        console.log('🔓 Ejecutando logout de Keycloak...');
        
        // Logout de Keycloak (esto redirigirá automáticamente)
        await keycloak.logout({
          redirectUri: `${window.location.origin}`
        });
      } else {
        console.log('⚠️ Keycloak no está disponible, redirigiendo manualmente...');
        // Si Keycloak no está disponible, redirigir manualmente
        window.location.href = '/login';
      }
      
    } catch (error) {
      console.error('❌ Error durante logout:', error);
      
      // En caso de error, asegurar que el estado local esté limpio
      setAuthenticated(false);
      setUser(null);
      setRoles([]);
      setError(null);
      
      // Forzar redirección en caso de error
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
      
      throw error; // Re-lanzar para que el componente pueda manejarlo
    }
  }, [keycloak, clearTokenRefresh]);

  // Configurar renovación automática de tokens - sin dependencias circulares
  const setupTokenRefresh = useCallback((kc) => {
    // Limpiar cualquier interval anterior
    clearTokenRefresh();
    
    // Crear nuevo interval
    refreshIntervalRef.current = setInterval(async () => {
      try {
        if (kc.authenticated) {
          const refreshed = await kc.updateToken(60);
          if (refreshed) {
            console.log('Token renovado automáticamente');
          }
        }
      } catch (error) {
        console.error('Error renovando token:', error);
        // Llamar al logout async para manejo completo de errores
        try {
          console.log('🔄 Token expirado, cerrando sesión...');
          
          // Limpiar estado inmediatamente
          setAuthenticated(false);
          setUser(null);
          setRoles([]);
          setError(null);
          setInitialized(false);
          clearTokenRefresh();
          
          // Limpiar storage de autenticación
          const keysToRemove = [
            'keycloak-token',
            'keycloak-refresh-token', 
            'keycloak-id-token',
            'keycloak-state',
            'keycloak-nonce',
            'kc-iss',
            'kc-auth-success',
            'KC_RESTART'
          ];
          
          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          });
          
          // Forzar redirección por token expirado
          window.location.href = '/login?reason=expired';
          
        } catch (logoutError) {
          console.error('Error en logout automático:', logoutError);
          // Fallback: forzar recarga de página
          window.location.reload();
        }
      }
    }, 4 * 60 * 1000);
  }, [clearTokenRefresh]);

  // Función para inicializar Keycloak solo cuando sea necesario
  const initializeKeycloak = useCallback(async () => {
    // Prevenir múltiples inicializaciones simultáneas
    if (initialized || keycloak || loading || initializingRef.current) {
      console.log('🔄 Keycloak ya inicializado o en proceso, retornando instancia existente');
      return keycloak;
    }

    initializingRef.current = true;
    setLoading(true);
    
    try {
      console.log('🔐 Inicializando Keycloak con config:', {
        url: config.keycloak.url,
        realm: config.keycloak.realm,
        clientId: config.keycloak.clientId
      });

      const kc = new Keycloak({
        url: config.keycloak.url,
        realm: config.keycloak.realm,
        clientId: config.keycloak.clientId
      });

      // URI de redirección principal configurada en Keycloak (raíz)
      const redirectUri = config.keycloak.getRedirectUri();

      console.log('🌐 URL de redirección calculada:', redirectUri);
      console.log('🌐 Hostname actual:', window.location.hostname);
      console.log('🔧 Intentando inicializar Keycloak...');

      // Configuración simplificada y más estable
      const initConfig = {
        onLoad: 'check-sso',
        // Deshabilitar silent check SSO para evitar errores de storage access
        // silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        silentCheckSsoFallback: false,
        checkLoginIframe: false,
        flow: 'standard',
        enableLogging: false,
        promiseType: 'native',
        timeoutInSeconds: 10
      };

      console.log('🔧 Config de inicialización:', initConfig);

      const auth = await kc.init(initConfig);

      console.log('✅ Keycloak.init() completado exitosamente');
      console.log('🔐 Estado de autenticación:', auth);
      console.log('🔐 Keycloak authenticated:', kc.authenticated);
      console.log('🔐 Keycloak token:', kc.token ? 'presente' : 'ausente');

      // Exponer keycloak globalmente
      window.keycloak = kc;

      setKeycloak(kc);
      setAuthenticated(auth);
      setInitialized(true);

      if (auth) {
        try {
          console.log('👤 Cargando perfil de usuario...');
          const userProfile = await kc.loadUserProfile();
          console.log('👤 Perfil cargado:', userProfile);
          
          const userRoles = kc.realmAccess?.roles || [];
          console.log('🔑 Roles encontrados:', userRoles);
          
          setUser({
            id: userProfile.id,
            username: userProfile.username,
            email: userProfile.email,
            firstName: userProfile.firstName,
            lastName: userProfile.lastName,
            fullName: `${userProfile.firstName} ${userProfile.lastName}`
          });
          
          setRoles(userRoles);
          
          // Configurar renovación automática de tokens
          setupTokenRefresh(kc);
          
          console.log('✅ Usuario y roles configurados exitosamente');
          // Loading completado DESPUÉS de cargar roles
          setLoading(false);
          
        } catch (profileError) {
          console.warn('⚠️ Error cargando perfil de usuario:', profileError);
          
          // Intentar obtener información básica del token directamente
          try {
            if (kc.token) {
              const tokenParsed = kc.tokenParsed;
              console.log('🔍 Intentando obtener info del token:', tokenParsed);
              
              if (tokenParsed) {
                setUser({
                  id: tokenParsed.sub,
                  username: tokenParsed.preferred_username || tokenParsed.name,
                  email: tokenParsed.email,
                  firstName: tokenParsed.given_name || 'Usuario',
                  lastName: tokenParsed.family_name || '',
                  fullName: tokenParsed.name || 'Usuario'
                });
                
                setRoles(kc.realmAccess?.roles || []);
                console.log('✅ Información del usuario obtenida del token');
              }
            }
          } catch (tokenError) {
            console.error('❌ Error obteniendo info del token:', tokenError);
          }
          
          setupTokenRefresh(kc);
          // Loading completado incluso con errores
          setLoading(false);
        }
      } else {
        // No autenticado, asegurar que roles esté vacío y terminar loading
        setRoles([]);
        setLoading(false);
      }
      
      setError(null);
      return kc;
      
    } catch (err) {
      console.error('❌ Error inicializando Keycloak:', err);
      
      // Marcar como inicializado para evitar bucles infinitos
      setInitialized(true);
      
      // Manejo simple de errores
      if (err && (err.error === 'invalid_client' || err.error === 'unauthorized_client')) {
        setError('Error de configuración de autenticación. Contacta al administrador.');
      } else {
        // No mostrar error para SSO fallido (normal)
        console.info('ℹ️ Silent SSO no disponible, continuando sin autenticar');
        setError(null);
      }
      
      return null;
    } finally {
      // No setear loading false aquí, se maneja específicamente arriba
      initializingRef.current = false;
    }
  }, [initialized, keycloak, loading, setupTokenRefresh]);

  // Login - inicializa Keycloak si es necesario
  const login = useCallback(async (redirectPath = '/') => {
    let kc = keycloak;
    
    if (!kc) {
      kc = await initializeKeycloak();
    }
    
    if (kc) {
      // Usar la URI que Keycloak espera (raíz) para evitar problemas con redirect_uri mismatch
      const redirectUri = `${window.location.origin}${redirectPath}`;
      
      console.log('🔑 Iniciando login con redirect URI:', redirectUri);
      
      kc.login({
        redirectUri: redirectUri
      });
    }
  }, [keycloak, initializeKeycloak]);

  // Función para verificar autenticación existente (para rutas protegidas)
  const checkAuthentication = useCallback(async () => {
    if (!initialized) {
      return await initializeKeycloak();
    }
    return keycloak;
  }, [initialized, keycloak, initializeKeycloak]);

  // Verificar si el usuario tiene un rol específico
  const hasRole = useCallback((role) => {
    return roles.includes(role);
  }, [roles]);

  // Verificar si es admin
  const isAdmin = useCallback(() => {
    return hasRole(USER_ROLES.ADMIN);
  }, [hasRole]);

  // Obtener token
  const getToken = useCallback(() => {
    return keycloak?.token;
  }, [keycloak]);

  // Actualizar token
  const updateToken = useCallback(async (minValidity = 30) => {
    if (keycloak && keycloak.authenticated) {
      try {
        const refreshed = await keycloak.updateToken(minValidity);
        if (refreshed) {
          console.log('Token renovado exitosamente');
        }
        return refreshed;
      } catch (error) {
        console.error('Error actualizando token:', error);
        logout();
        return false;
      }
    }
    return false;
  }, [keycloak, logout]);

  // Verificar estado de autenticación
  const checkAuth = useCallback(() => {
    return keycloak?.authenticated || false;
  }, [keycloak]);

  return {
    keycloak,
    authenticated,
    loading,
    user,
    roles,
    error,
    initialized,
    login,
    logout,
    hasRole,
    isAdmin,
    getToken,
    updateToken,
    checkAuth,
    checkAuthentication,
    initializeKeycloak
  };
};

export default useAuth; 